import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Mail, 
  Download, 
  Users,
  LogOut,
  ArrowLeft,
  Calendar,
  Loader2,
  Sheet,
  Link as LinkIcon,
  Unlink,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, credentials: 'include', headers });
};

export const NewsletterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribers, setSubscribers] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [sheetsStatus, setSheetsStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [spreadsheetId, setSpreadsheetId] = useState('');

  // Check for sheets_connected query param
  useEffect(() => {
    if (searchParams.get('sheets_connected') === 'true') {
      setSyncResult({ success: true, message: 'Google Sheets connected successfully!' });
      // Clear the query param
      window.history.replaceState({}, '', '/cms/newsletter');
    }
  }, [searchParams]);

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/auth/me`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.role !== 'admin') {
            navigate('/cms');
            return;
          }
          setUser(data);
        } else {
          navigate('/cms/login');
        }
      } catch (error) {
        navigate('/cms/login');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  // Fetch subscribers and sheets status
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subscribersRes, sheetsRes] = await Promise.all([
          authFetch(`${API_URL}/api/admin/newsletter/subscribers`),
          authFetch(`${API_URL}/api/admin/sheets/status`)
        ]);
        
        if (subscribersRes.ok) {
          setSubscribers(await subscribersRes.json());
        }
        if (sheetsRes.ok) {
          const status = await sheetsRes.json();
          setSheetsStatus(status);
          if (status.spreadsheet_id) {
            setSpreadsheetId(status.spreadsheet_id);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/newsletter/export`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'newsletter_subscribers.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleConnectSheets = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/oauth/sheets/login`);
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      } else {
        const error = await response.json();
        setSyncResult({ success: false, message: error.detail || 'Failed to initiate connection' });
      }
    } catch (error) {
      console.error('Connect error:', error);
      setSyncResult({ success: false, message: 'Failed to connect to Google Sheets' });
    }
  };

  const handleDisconnectSheets = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/sheets/disconnect`, {
        method: 'POST'
      });
      if (response.ok) {
        setSheetsStatus({ ...sheetsStatus, connected: false });
        setSyncResult({ success: true, message: 'Google Sheets disconnected' });
      }
    } catch (error) {
      console.error('Disconnect error:', error);
    }
  };

  const handleSyncToSheets = async () => {
    if (!spreadsheetId) {
      setSyncResult({ success: false, message: 'Please enter a Google Spreadsheet URL or ID first' });
      return;
    }

    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await authFetch(`${API_URL}/api/admin/sheets/sync-newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spreadsheet_id: spreadsheetId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSyncResult({ success: true, message: `Synced ${data.synced} subscribers to Google Sheets` });
      } else {
        const error = await response.json();
        setSyncResult({ success: false, message: error.detail || 'Sync failed' });
      }
    } catch (error) {
      console.error('Sync error:', error);
      setSyncResult({ success: false, message: 'Failed to sync to Google Sheets' });
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    localStorage.removeItem('user');
    navigate('/cms/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const recentSubscribers = subscribers.filter(s => {
    const date = new Date(s.created_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return date >= thirtyDaysAgo;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              CMS
            </span>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-8 h-8 rounded-full"
                />
              )}
              <div className="text-sm">
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </header>

      <div className="editorial-container py-8">
        {/* Back button */}
        <Link to="/cms" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={16} />
          Back to Dashboard
        </Link>

        {/* Sync Result Alert */}
        {syncResult && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            syncResult.success ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
          }`}>
            {syncResult.success ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            {syncResult.message}
            <button 
              onClick={() => setSyncResult(null)} 
              className="ml-auto text-sm hover:opacity-70"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Users size={24} className="text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{subscribers.length}</p>
                <p className="text-sm text-muted-foreground">Total Subscribers</p>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <Mail size={24} className="text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{recentSubscribers.length}</p>
                <p className="text-sm text-muted-foreground">Last 30 Days</p>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${sheetsStatus?.connected ? 'bg-green-500/10' : 'bg-muted'}`}>
                <Sheet size={24} className={sheetsStatus?.connected ? 'text-green-500' : 'text-muted-foreground'} />
              </div>
              <div>
                <p className="text-lg font-bold">{sheetsStatus?.connected ? 'Connected' : 'Not Connected'}</p>
                <p className="text-sm text-muted-foreground">Google Sheets</p>
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/10 rounded-lg">
                <Calendar size={24} className="text-blue-500" />
              </div>
              <div>
                <p className="text-lg font-bold">
                  {subscribers[0] ? new Date(subscribers[0].created_at).toLocaleDateString() : 'N/A'}
                </p>
                <p className="text-sm text-muted-foreground">Latest Signup</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Google Sheets Integration */}
        <Card className="p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium flex items-center gap-2">
                <Sheet size={20} />
                Google Sheets Integration
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Sync your newsletter subscribers to a Google Sheet for easy management
              </p>
            </div>
          </div>

          {/* Spreadsheet ID Configuration - Always visible */}
          <div className="mb-6 p-4 bg-muted/30 rounded-lg">
            <label className="block text-sm font-medium mb-2">Google Spreadsheet Link or ID</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={spreadsheetId}
                onChange={(e) => {
                  // Extract spreadsheet ID from full URL if pasted
                  let value = e.target.value;
                  const match = value.match(/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
                  if (match) {
                    value = match[1];
                  }
                  setSpreadsheetId(value);
                }}
                placeholder="Paste Google Sheets URL or Spreadsheet ID"
                className="flex-1 px-3 py-2 bg-background border border-border rounded text-sm"
              />
              {spreadsheetId && (
                <a
                  href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 bg-muted hover:bg-muted/80 rounded text-sm flex items-center gap-1"
                >
                  <ExternalLink size={14} />
                  Open
                </a>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Create a new Google Sheet, then paste the URL here. Example: https://docs.google.com/spreadsheets/d/ABC123.../edit
            </p>
          </div>

          {!sheetsStatus?.has_credentials && (
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg mb-4">
              <p className="text-sm text-yellow-500">
                <strong>Setup Required:</strong> Add GOOGLE_SHEETS_CLIENT_ID, GOOGLE_SHEETS_CLIENT_SECRET, and GOOGLE_SHEETS_REDIRECT_URI to your backend .env file. 
                <a 
                  href="https://console.cloud.google.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline ml-1"
                >
                  Get credentials from Google Cloud Console
                </a>
              </p>
            </div>
          )}

          {sheetsStatus?.has_credentials && (
            <div className="space-y-4">
              {!sheetsStatus.connected ? (
                <Button onClick={handleConnectSheets} className="gap-2">
                  <LinkIcon size={16} />
                  Connect Google Account
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSyncToSheets} 
                    disabled={syncing || !spreadsheetId}
                    className="gap-2"
                  >
                    {syncing ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <RefreshCw size={16} />
                    )}
                    Sync to Sheets
                  </Button>
                  <Button 
                    onClick={handleDisconnectSheets} 
                    variant="outline"
                    className="gap-2"
                  >
                    <Unlink size={16} />
                    Disconnect
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Actions */}
        <div className="flex gap-4 mb-8">
          <Button onClick={handleExport} disabled={exporting} variant="outline" className="gap-2">
            {exporting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            Export CSV
          </Button>
        </div>

        {/* Subscribers List */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-medium">All Subscribers</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Source</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Subscribed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {subscribers.map((sub, idx) => (
                  <tr key={sub.subscription_id || idx} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-sm">{sub.email}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground capitalize">{sub.source || 'website'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {new Date(sub.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {subscribers.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">
                      No subscribers yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default NewsletterPage;
